import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Bookmark, ExternalLink, CheckCircle2, AlertCircle, Info, Filter } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useA11y } from '../context/A11yContext';
import { useLang } from '../context/LangContext';
import { ProviderList } from '../components/ProviderList';
import { LocalPrograms } from '../components/LocalPrograms';
import { ListenButton } from '../components/ListenButton';

const HAS_PROVIDERS = ['adp_wheelchair', 'cohb', 'rgi_toronto'];

const CATEGORIES = ['All', 'Money & Income', 'Tax & Credits', 'Disability & Medical', 'Housing & Living', 'Health & Dental'];

const badgeCfg = {
  high: { label: 'High match', bg: 'bg-[#E6F4EA] text-[#0F5132] border-[#A3CFBB]' },
  possible: { label: 'Possible match', bg: 'bg-[#FFF4E5] text-[#663C00] border-[#FFE0B2]' },
  needs_info: { label: 'Needs more info', bg: 'bg-[#EBF3FA] text-[#13426B] border-[#B8D5E5]' },
};

export default function Results() {
  const navigate = useNavigate();
  const { plainLanguage } = useA11y();
  const { t, lang } = useLang();
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState('All');
  const [tracked, setTracked] = useState({});
  const [postal, setPostal] = useState('');

  useEffect(() => {
    const cached = sessionStorage.getItem('match_results');
    if (cached) setResults(JSON.parse(cached));
    else navigate('/find-benefits');
    api.get('/profile').then(({ data }) => setPostal(data.postal_code || '')).catch(() => {});
    api.get('/applications').then(({ data }) => {
      const m = {};
      data.applications.forEach((a) => { m[a.program_id] = a; });
      setTracked(m);
    }).catch(() => {});
  }, [navigate]);

  const filtered = filter === 'All' ? results : results.filter((r) => r.category === filter);
  const counts = { high: 0, possible: 0, needs_info: 0 };
  results.forEach((r) => counts[r.match_strength]++);

  const trackIt = async (p) => {
    try {
      const { data } = await api.post('/applications', { program_id: p.id, status: 'researching' });
      setTracked((t) => ({ ...t, [p.id]: data }));
      toast.success(`Added "${p.name}" to My Applications`);
    } catch { toast.error('Please sign in to track applications'); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>{t('Your personalized benefits')}</h1>
          <p className="text-muted-foreground mt-2">{t('We found')} <span className="text-primary font-semibold">{results.length} {t('programs')}</span> {t('to investigate. This is a screening, not an eligibility decision — the government makes the final call.')}</p>
          {lang === 'fr' && results.some((r) => r.machine_translated) && <p className="text-xs text-muted-foreground mt-1" data-testid="mt-note">{t('Machine-translated by AI. Official program names and amounts follow government sources.')}</p>}
        </div>
        {results.length > 0 && <div className="flex gap-2 flex-wrap">
          <Badge variant="outline" className="bg-[#E6F4EA] text-[#0F5132] border-[#A3CFBB]" data-testid="count-high">{counts.high} {t('High')}</Badge>
          <Badge variant="outline" className="bg-[#FFF4E5] text-[#663C00] border-[#FFE0B2]" data-testid="count-possible">{counts.possible} {t('Possible')}</Badge>
          <Badge variant="outline" className="bg-[#EBF3FA] text-[#13426B] border-[#B8D5E5]" data-testid="count-needsinfo">{counts.needs_info} {t('Needs info')}</Badge>
        </div>}
      </div>

      <LocalPrograms postal={postal} onPostalSaved={(p) => api.get('/profile').then(({ data }) => api.post('/profile', { ...data, postal_code: p })).catch(() => {})} />

      <div className="flex items-center gap-2 flex-wrap mb-6">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {CATEGORIES.map((c) => (
          <Button key={c} size="sm" variant={filter === c ? 'default' : 'outline'} className={filter === c ? 'bg-primary text-primary-foreground' : ''} onClick={() => setFilter(c)} data-testid={`filter-${c.replace(/\s+/g, '-').toLowerCase()}`}>{t(c)}</Button>
        ))}
      </div>

      <div className="grid gap-4">
        {filtered.map((p) => {
          const cfg = badgeCfg[p.match_strength];
          const isTracked = !!tracked[p.id];
          return (
            <Card key={p.id} className="p-5 sm:p-6 bg-card" data-testid={`result-card-${p.id}`}>
              <div className="flex flex-col md:flex-row gap-4 md:items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge className={`${cfg.bg} border font-medium`}>{t(cfg.label)}</Badge>
                    <Badge variant="outline">{p.government}</Badge>
                    <Badge variant="outline">{t(p.category)}</Badge>
                  </div>
                  <h3 className="text-xl font-bold" style={{fontFamily: 'Lexend'}}>{p.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5">{plainLanguage ? p.plain_language : p.summary}</p>
                  <ListenButton text={`${p.name}. ${plainLanguage ? p.plain_language : p.summary} ${t('Est. value:')} ${p.estimated_amount}.`} label={p.name} testId={`listen-${p.id}`} />
                  <div className="mt-3 flex items-center gap-4 flex-wrap text-sm">
                    <div><span className="text-muted-foreground">{t('Est. value:')}</span> <span className="font-semibold text-primary">{p.estimated_amount}</span></div>
                    <div className="text-muted-foreground">{p.effort}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {isTracked ? (
                    <Button variant="outline" disabled data-testid={`tracked-${p.id}`}><CheckCircle2 className="w-4 h-4 mr-2 text-primary" /> {t('Tracking')}</Button>
                  ) : (
                    <Button onClick={() => trackIt(p)} variant="outline" data-testid={`track-${p.id}`}><Bookmark className="w-4 h-4 mr-2" /> {t('Track')}</Button>
                  )}
                  <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid={`apply-${p.id}`}>
                    <a href={p.application_url} target="_blank" rel="noopener noreferrer">{t('Apply')} <ExternalLink className="w-4 h-4 ml-2" /></a>
                  </Button>
                </div>
              </div>

              <Accordion type="single" collapsible className="mt-4">
                <AccordionItem value="why" className="border-t border-border pt-2">
                  <AccordionTrigger data-testid={`why-${p.id}`} className="text-sm font-medium">{t('Why was I matched?')}</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t('Based on your answers')}</div>
                        <ul className="text-sm space-y-1">
                          {p.match_reasons.length ? p.match_reasons.map((r, i) => <li key={i} className="text-foreground/80">• {r}</li>) : <li className="text-muted-foreground italic">Add more profile info for stronger matches.</li>}
                        </ul>
                      </div>
                      <div>
                        <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-amber-600" /> {t('Still to confirm')}</div>
                        <ul className="text-sm space-y-1">
                          {p.missing_info.length ? p.missing_info.map((r, i) => <li key={i} className="text-foreground/80">• {r}</li>) : <li className="text-muted-foreground italic">Nothing outstanding on our side.</li>}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 p-3 rounded-lg bg-secondary text-sm flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                      <span>Documents you'll likely need: {p.documents.join(', ')}. Official source: <a className="underline" href={p.application_url} target="_blank" rel="noopener noreferrer">{p.agency}</a>. Last verified {p.last_verified}.</span>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {HAS_PROVIDERS.includes(p.id) && (
                  <AccordionItem value="providers" className="border-t border-border pt-2">
                    <AccordionTrigger data-testid={`providers-toggle-${p.id}`} className="text-sm font-medium">Local providers &amp; referral agencies (Toronto)</AccordionTrigger>
                    <AccordionContent><ProviderList programId={p.id} /></AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
